/**
 * Run difficulty configurations for Heavenly Mountain Resort.
 *
 * Grade reference:
 *   Green  ~8-15% grade  (~5-8 degrees)
 *   Blue   ~20-35% grade (~11-19 degrees)
 *   Black  ~35-55% grade (~19-29 degrees)
 *   Double Black ~55-80%+ grade (~29-39+ degrees)
 */

export type RunDifficulty = 'green' | 'blue' | 'black' | 'doubleBlack'

export interface RunConfig {
  name: string
  difficulty: RunDifficulty
  grade: number          // slope grade (rise/run ratio)
  maxSpeed: number       // m/s
  width: number          // meters
  length: number         // meters
  terrainNoise: number   // bump intensity
  jumps: boolean         // Olympic-style jump ramps on the run?
  moguls: boolean        // mogul field — crash if going too fast over them
  obstacleTrees: boolean // trees randomly on the run to dodge?
}

export const RUNS: Record<string, RunConfig> = {
  // === GREEN ===
  'enchanted-forest': {
    name: 'Enchanted Forest',
    difficulty: 'green',
    grade: 0.12,
    maxSpeed: 10,       // ~22 mph
    width: 50,
    length: 600,
    terrainNoise: 0.15,

    jumps: false,
    obstacleTrees: true,
    moguls: false,
  },
  'big-easy': {
    name: 'Big Easy',
    difficulty: 'green',
    grade: 0.10,
    maxSpeed: 10,
    width: 55,
    length: 500,
    terrainNoise: 0.1,

    jumps: false,
    obstacleTrees: true,
    moguls: false,
  },

  // === BLUE ===
  'patsys': {
    name: "Patsy's",
    difficulty: 'blue',
    grade: 0.22,
    maxSpeed: 16,       // ~35 mph
    width: 40,
    length: 800,
    terrainNoise: 0.3,

    jumps: false,
    obstacleTrees: true,
    moguls: false,
  },
  'california-trail': {
    name: 'California Trail',
    difficulty: 'blue',
    grade: 0.25,
    maxSpeed: 18,       // ~40 mph
    width: 35,
    length: 1600,       // ~1 mile, longest run at Heavenly
    terrainNoise: 0.35,

    jumps: false,
    obstacleTrees: true,
    moguls: false,
  },
  'ridge-run': {
    name: 'Ridge Run',
    difficulty: 'blue',
    grade: 0.28,
    maxSpeed: 18,
    width: 30,
    length: 900,
    terrainNoise: 0.3,

    jumps: false,
    obstacleTrees: true,
    moguls: false,
  },

  // === BLACK DIAMOND ===
  'gunbarrel': {
    name: 'Gunbarrel',
    difficulty: 'black',
    grade: 0.45,
    maxSpeed: 25,       // ~56 mph
    width: 25,
    length: 700,
    terrainNoise: 0.5,

    jumps: true,
    obstacleTrees: true,
    moguls: true,        // famous mogul run!
  },
  'east-bowl': {
    name: 'East Bowl',
    difficulty: 'black',
    grade: 0.40,
    maxSpeed: 24,
    width: 50,
    length: 600,
    terrainNoise: 0.6,

    jumps: false,
    obstacleTrees: true,
    moguls: true,        // bumpy bowl
  },
  'galaxy': {
    name: 'Galaxy',
    difficulty: 'black',
    grade: 0.42,
    maxSpeed: 25,
    width: 30,
    length: 650,
    terrainNoise: 0.5,

    jumps: false,
    obstacleTrees: true,
    moguls: false,
  },

  // === DOUBLE BLACK DIAMOND ===
  'mott-canyon': {
    name: 'Mott Canyon',
    difficulty: 'doubleBlack',
    grade: 0.65,
    maxSpeed: 30,       // ~67 mph
    width: 15,
    length: 500,
    terrainNoise: 0.8,

    jumps: false,
    obstacleTrees: true,
    moguls: false,
  },
  'killebrew-canyon': {
    name: 'Killebrew Canyon',
    difficulty: 'doubleBlack',
    grade: 0.60,
    maxSpeed: 28,
    width: 18,
    length: 550,
    terrainNoise: 0.75,

    jumps: false,
    obstacleTrees: true,
    moguls: false,
  },
  'pinnacles': {
    name: 'Pinnacles',
    difficulty: 'doubleBlack',
    grade: 0.70,
    maxSpeed: 30,
    width: 12,
    length: 400,
    terrainNoise: 0.9,

    jumps: false,
    obstacleTrees: true,
    moguls: false,
  },
  // === SPECIAL ===
  'liams-run': {
    name: "Liam's Run",
    difficulty: 'doubleBlack',
    grade: 0.80,           // insanely steep
    maxSpeed: 40,          // ~89 mph
    width: 30,
    length: 1000,
    terrainNoise: 1.0,

    jumps: true,           // random jump ramps everywhere
    obstacleTrees: true,   // trees ON the run to dodge
    moguls: false,
  },
}

// Default run
export const DEFAULT_RUN = 'patsys'
